import apiSlice from '../api/api.slice';
import {
  IIPostWithUserAndAttachmentsRequestParams,
  IPost,
  IPostAttachment,
  IPostAutoComplete,
  IPostDataUploadRequest,
  IPostDetailResponse,
  IPostDraft,
  IPostWithUserAndAttachments,
} from './post';

export const postApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPosts: builder.query<
      IPostWithUserAndAttachments[],
      IIPostWithUserAndAttachmentsRequestParams
    >({
      query: (params) => ({
        url: '/api/v1/post',
        method: 'GET',
        params,
      }),
      transformResponse: (response: { posts: IPostWithUserAndAttachments[] }) =>
        response.posts,
    }),
    getPostById: builder.query<IPostDetailResponse, string>({
      query: (id) => ({
        url: `/api/v1/post/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { post: IPostDetailResponse }) =>
        response.post,
    }),
    getSearchAutoComplete: builder.query<
      IPostAutoComplete[],
      { searchQuery: string }
    >({
      query: (params) => ({
        url: `/api/v1/post/search/autoComplete`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: { posts: IPostAutoComplete[] }) =>
        response.posts,
    }),
    getPostByIdAndApiKey: builder.query<
      IPostDetailResponse,
      { postId: string; apiKey: string }
    >({
      query: ({ postId, apiKey }) => ({
        url: `/api/v1/post/preview/postDetails/?postId=${postId}`,
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
        },
      }),
      transformResponse: (response: { post: IPostDetailResponse }) =>
        response.post,
    }),
    getPostByUserId: builder.query<IPostWithUserAndAttachments[], string>({
      query: (id) => ({
        url: `/api/v1/post/getPostsByUserId/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { posts: IPostWithUserAndAttachments[] }) =>
        response.posts,
    }),
    getPostByUsername: builder.query<IPostWithUserAndAttachments[], string>({
      query: (username) => ({
        url: `/api/v1/post/getPostsByUsername/${username}`,
        method: 'GET',
      }),
      transformResponse: (response: { posts: IPostWithUserAndAttachments[] }) =>
        response.posts,
    }),
    createPost: builder.mutation<
      {
        post: IPost;
        attachments: IPostAttachment[];
      },
      FormData
    >({
      query: (data) => ({
        url: '/api/v1/post/create',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: {
        post: {
          post: IPost;
          attachments: IPostAttachment[];
        };
      }) => response.post,
    }),
    createDraftPost: builder.mutation<IPostDraft, FormData>({
      query: (body) => ({
        url: '/api/v1/post/createPost',
        method: 'POST',
        body,
      }),
      transformResponse: (response: {
        post: IPostDraft;
        attachments: IPostAttachment;
      }) => response.post,
    }),
    updatePostData: builder.mutation<
      Partial<IPost>,
      { postId: string; data: IPostDataUploadRequest }
    >({
      query: ({ postId, data }) => ({
        url: `/api/v1/post/updatePost/${postId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: { post: Partial<IPost> }) => response.post,
    }),
    updatePostAttachment: builder.mutation<
      IPostAttachment[],
      { postId: string; currentPage: number; data: FormData }
    >({
      query: ({ postId, currentPage, data }) => ({
        url: `/api/v1/post/updateAttachments/${postId}`,
        method: 'PUT',
        body: data,
        params: {
          currentPage,
        },
      }),
      transformResponse: (response: { attachments: IPostAttachment[] }) =>
        response.attachments,
    }),
    deletePostDelete: builder.mutation<undefined, string>({
      query: (postId) => ({
        url: `/api/v1/post/delete/${postId}`,
        method: 'DELETE',
      }),
    }),
  }),
});

apiSlice.enhanceEndpoints({
  addTagTypes: ['Post', 'UserPosts'],
  endpoints: {
    getPosts: {
      providesTags: ['Post'],
    },
    getPostById: {
      providesTags: ['Post'],
    },
    getPostByUserId: {
      providesTags: ['UserPosts'],
    },
    getPostByUsername: {
      providesTags: ['UserPosts'],
    },
    createPost: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    deletePostDelete: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    updatePostAttachment: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    updatePostData: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
    createDraftPost: {
      invalidatesTags: ['Post', 'UserPosts'],
    },
  },
});

export const {
  useCreatePostMutation,
  useGetPostsQuery,
  useLazyGetPostsQuery,
  useGetPostByIdQuery,
  useGetPostByUserIdQuery,
  useGetPostByIdAndApiKeyQuery,
  useUpdatePostAttachmentMutation,
  useUpdatePostDataMutation,
  useCreateDraftPostMutation,
  useLazyGetPostByIdQuery,
  useDeletePostDeleteMutation,
  useLazyGetSearchAutoCompleteQuery,
  useGetPostByUsernameQuery,
} = postApiSlice;
